import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Target, History, Check, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const AREAS = [
  "Client Experience at Telecaller Enquiry", "Client Experience at Walk-in",
  "Client Experience at Doctor Consultation", "Pre-Op Counseling Quality",
  "Post-Op Follow-up Discipline", "On-Floor Issue Resolution", "Pending Dues Clarity",
  "Upselling & Cross-Selling", "Complications Handling", "Pricing & Discount Control",
  "Look & Feel of Youngness Centre",
];

export default function Goals() {
  const { user } = useAuth();
  const { branch } = useApp();
  const [goals, setGoals] = useState([]);
  const [edits, setEdits] = useState({});
  const [changelog, setChangelog] = useState([]);
  const [logOpen, setLogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/goals/scorecard", { params: branch !== "All" ? { branch } : {} });
      setGoals(res.data);
    } catch {}
  }, [branch]);

  useEffect(() => { load(); }, [load]);

  const save = async (g) => {
    const newVal = edits[g.key];
    if (newVal === undefined || Number(newVal) === g.target) return;
    try {
      await api.put(`/goals/${g.key}`, { target: Number(newVal) });
      toast.success(`${g.label} updated`);
      setEdits((e) => { const n = { ...e }; delete n[g.key]; return n; });
      load();
    } catch { toast.error("Update failed"); }
  };

  const saveOwner = async (key, owner) => {
    try {
      await api.put(`/goals/${key}`, { owner });
      toast.success("Owner updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const openLog = async () => {
    try {
      const res = await api.get("/goals/changelog");
      setChangelog(res.data);
      setLogOpen(true);
    } catch { toast.error("Founder access only"); }
  };

  return (
    <div>
      <PageHeader title="Goals & Objectives"
        subtitle="Own the patient's experience from first contact to long-term follow-up. Targets are yours to adjust — every change is logged.">
        {user?.role === "founder" && (
          <Button data-testid="view-changelog" onClick={openLog} variant="outline" className="bg-transparent border-white/10 text-slate-200 hover:bg-[#1E293B] hover:text-white">
            <History className="h-4 w-4 mr-2" /> Change Log
          </Button>
        )}
      </PageHeader>

      <Card className="p-5 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-4 w-4 text-[#C9A24B]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Mission</span>
        </div>
        <p className="text-slate-200 leading-relaxed max-w-4xl">
          Own the patient's experience from first contact to long-term follow-up — so every touchpoint (pricing, reception,
          doctor consultation, pre-surgery, post-surgery) feels consistent and cared-for.
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="lg:col-span-2 flex items-center gap-2 mb-1">
          <TrendingUp className="h-4 w-4 text-[#C9A24B]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Live KPI Scorecard — editable targets</span>
        </div>
        {goals.map((g) => {
          const changed = edits[g.key] !== undefined && Number(edits[g.key]) !== g.target;
          const ragColor = g.rag === "green" ? "#059669" : g.rag === "amber" ? "#C9A24B" : g.rag === "red" ? "#DC2626" : "#64748B";
          const unitLabel = g.unit && g.unit.startsWith("/") ? g.unit : g.unit ? ` ${g.unit}` : "";
          return (
            <Card key={g.key} data-testid="goal-card" className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span data-testid={`goal-rag-${g.key}`} className="h-2 w-2 rounded-full" style={{ background: ragColor }} />
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#C9A24B]">{g.area}</p>
                  </div>
                  <p className="text-white mt-1 text-sm">{g.label}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-slate-400">Actual <span data-testid={`goal-actual-${g.key}`} className="font-mono-ync font-semibold" style={{ color: ragColor }}>{g.actual}{unitLabel}</span></span>
                    <span className="text-xs text-slate-500">Target <span className="font-mono-ync text-slate-300">{g.target}</span></span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid={`goal-input-${g.key}`} type="number"
                    value={edits[g.key] ?? g.target}
                    onChange={(e) => setEdits((prev) => ({ ...prev, [g.key]: e.target.value }))}
                    className="w-24 bg-[#0F172A] border-white/10 text-white font-mono-ync text-lg text-center" />
                  <Button data-testid={`goal-save-${g.key}`} size="icon" onClick={() => save(g)} disabled={!changed}
                    className={`h-9 w-9 ${changed ? "bg-[#059669] hover:bg-[#047857]" : "bg-[#1E293B] text-slate-600"}`}>
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">Concerned Person</span>
                <Select value={g.owner || "Relationship Manager"} onValueChange={(v) => saveOwner(g.key, v)}>
                  <SelectTrigger data-testid={`goal-owner-${g.key}`} className="h-8 w-52 bg-[#0F172A] border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                    {["Relationship Manager", "Founder", "Branch Head", "Reception", "Doctor"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-5">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Responsibility Areas</span>
        <div className="flex flex-wrap gap-2 mt-3">
          {AREAS.map((a) => <Badge key={a} className="bg-[#0F172A] border border-white/5 text-slate-300 font-normal py-1">{a}</Badge>)}
        </div>
      </Card>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display font-light text-2xl">Target Change Log</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {changelog.length === 0 && <p className="text-slate-500 text-sm py-4">No changes recorded yet.</p>}
            {changelog.map((c) => (
              <div key={c.id} className="p-3 rounded-lg bg-[#0F172A] border border-white/5 text-sm">
                <div className="flex justify-between">
                  <span className="text-white">{c.label}</span>
                  <span className="text-slate-500 text-xs">{c.changed_at?.slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-slate-400 mt-1">
                  <span className="text-[#f87171] font-mono-ync">{c.old_value}</span> → <span className="text-[#34d399] font-mono-ync">{c.new_value}</span>
                  <span className="text-slate-500"> · by {c.changed_by} ({c.changed_by_role})</span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
